import React from 'react';
import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { StaffingRow, DeptSummary, CompanyTotals } from '../../../services/p9306Api';

const { Text } = Typography;

interface ResultTableProps {
  data: StaffingRow[];
  summary: DeptSummary[];
  totals: CompanyTotals | null;
  loading: boolean;
}

export const ResultTable = React.memo(({ data, summary, totals, loading }: ResultTableProps) => {
  const detailColumns: ColumnsType<StaffingRow> = [
    { title: '部門代號', dataIndex: 'dept_code', key: 'dept_code', width: 90 },
    { title: '部門名稱', dataIndex: 'dept_name', key: 'dept_name', width: 140 },
    { title: '職稱代號', dataIndex: 'title_code', key: 'title_code', width: 90 },
    { title: '職稱名稱', dataIndex: 'title_name', key: 'title_name', width: 140 },
    {
      title: '編制人數',
      dataIndex: 'planned_count',
      key: 'planned_count',
      width: 90,
      align: 'right' as const,
    },
    {
      title: '實際人數',
      dataIndex: 'actual_count',
      key: 'actual_count',
      width: 90,
      align: 'right' as const,
      render: (val: number, row: StaffingRow) => {
        const diff = val - row.planned_count;
        const color = diff > 0 ? '#f5222d' : diff < 0 ? '#faad14' : undefined;
        return <Text style={{ color }}>{val}</Text>;
      },
    },
    {
      title: '差異',
      key: 'diff',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, row: StaffingRow) => {
        const diff = row.actual_count - row.planned_count;
        const color = diff > 0 ? '#f5222d' : diff < 0 ? '#faad14' : '#52c41a';
        return <Text style={{ color }}>{diff > 0 ? `+${diff}` : diff}</Text>;
      },
    },
    { title: '員工編號', dataIndex: 'emp_no', key: 'emp_no', width: 100 },
    { title: '員工姓名', dataIndex: 'emp_name', key: 'emp_name', width: 100 },
    {
      title: '到職日期',
      dataIndex: 'hire_date',
      key: 'hire_date',
      width: 110,
      render: (v: string) => v?.substring(0, 10) || '',
    },
  ];

  const summaryColumns: ColumnsType<DeptSummary> = [
    { title: '部門代號', dataIndex: 'dept_code', key: 'dept_code', width: 90 },
    { title: '部門名稱', dataIndex: 'dept_name', key: 'dept_name', width: 140 },
    { title: '部門編制人數', dataIndex: 'dept_planned', key: 'dept_planned', width: 110, align: 'right' as const },
    { title: '部門核准人數', dataIndex: 'dept_approved', key: 'dept_approved', width: 110, align: 'right' as const },
    { title: '職稱編制合計', dataIndex: 'total_planned', key: 'total_planned', width: 110, align: 'right' as const },
    {
      title: '實際在職合計',
      dataIndex: 'total_actual',
      key: 'total_actual',
      width: 110,
      align: 'right' as const,
      render: (val: number, row: DeptSummary) => {
        const diff = val - row.total_planned;
        const color = diff > 0 ? '#f5222d' : diff < 0 ? '#faad14' : undefined;
        return <Text style={{ color }}>{val}</Text>;
      },
    },
    {
      title: '差異',
      key: 'diff',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, row: DeptSummary) => {
        const diff = row.total_actual - row.total_planned;
        const color = diff > 0 ? '#f5222d' : diff < 0 ? '#faad14' : '#52c41a';
        return <Text style={{ color }}>{diff > 0 ? `+${diff}` : diff}</Text>;
      },
    },
  ];

  return (
    <div>
      <h4 style={{ marginTop: 0 }}>編制明細</h4>
      <Table<StaffingRow>
        columns={detailColumns}
        dataSource={data}
        rowKey={(r, i) => `${r.dept_code}-${r.title_code}-${r.emp_no || ''}-${i}`}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
        scroll={{ x: 1100 }}
      />

      {summary.length > 0 && (
        <>
          <h4>部門彙總</h4>
          <Table<DeptSummary>
            columns={summaryColumns}
            dataSource={summary}
            rowKey="dept_code"
            loading={loading}
            size="small"
            pagination={false}
            scroll={{ x: 800 }}
            summary={() =>
              totals ? (
                <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    全公司合計（{totals.total_depts} 個部門）
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">{totals.total_planned}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">{totals.total_approved}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">—</Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">{totals.total_actual}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    {(() => {
                      const diff = totals.total_actual - totals.total_planned;
                      const color = diff > 0 ? '#f5222d' : diff < 0 ? '#faad14' : '#52c41a';
                      return <Text style={{ color, fontWeight: 'bold' }}>{diff > 0 ? `+${diff}` : diff}</Text>;
                    })()}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null
            }
          />
        </>
      )}
    </div>
  );
});

ResultTable.displayName = 'ResultTable';
